/**
 * SuggestionReviewModal (TCRM-71)
 *
 * Opens an AppModal for reviewing a pending MessageSuggestion.
 * The admin can edit the body, then approve (sends the message) or reject.
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { AppModal, AppConfirmModal } from './AppModal';
import { AppButton } from './AppButton';
import { useToast } from './AppToast';
import { adminSuggestionsApi } from '@/api';
import { useBusinessStore } from '@/stores';
import { colors, fonts, fontWeights, radius, spacing } from '@/theme/tokens';
import type { MessageSuggestionDTO } from '@tilly/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SEGMENT_COLORS: Record<string, string> = {
  vip: colors.brass ?? '#b8860b',
  lapsed: colors.warning ?? '#e67e22',
  frequent: colors.accent ?? '#3498db',
  new: colors.success ?? '#27ae60',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SuggestionReviewModalProps {
  open: boolean;
  suggestion: MessageSuggestionDTO | null;
  onClose: () => void;
  onReviewComplete?: (suggestionId: string, action: 'approve' | 'reject') => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SuggestionReviewModal({
  open,
  suggestion,
  onClose,
  onReviewComplete,
}: SuggestionReviewModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const translateSuggestionModal = (key: string, options?: Record<string, unknown>) =>
    t(key as never, options as never) as unknown as string;

  const [body, setBody] = useState(suggestion?.body ?? '');
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);

  // Reset body when a different suggestion is opened (render-phase update, no effect needed)
  const prevIdRef = useRef(suggestion?.id);
  if (prevIdRef.current !== suggestion?.id) {
    prevIdRef.current = suggestion?.id;
    setBody(suggestion?.body ?? '');
  }

  const isDirty = suggestion !== null && body !== suggestion.body;

  const reviewMutation = useMutation({
    mutationFn: ({ action, editedBody }: { action: 'approve' | 'reject'; editedBody?: string }) => {
      if (!activeBusinessId || !suggestion) {
        throw new Error(translateSuggestionModal('admin:suggestionModal.errors.missingContext'));
      }
      return adminSuggestionsApi.reviewSuggestion(activeBusinessId, suggestion.id, {
        action,
        body: action === 'approve' && editedBody ? editedBody : undefined,
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['suggestions', activeBusinessId] });
      if (variables.action === 'approve') {
        showSuccess(translateSuggestionModal('admin:suggestionModal.toasts.sent'));
      } else {
        showSuccess(translateSuggestionModal('admin:suggestionModal.toasts.rejected'));
      }
      if (suggestion) {
        onReviewComplete?.(suggestion.id, variables.action);
      }
      onClose();
    },
    onError: (err: Error) => {
      showError(
        err.message || translateSuggestionModal('admin:suggestionModal.errors.actionFailed')
      );
    },
  });

  const handleApprove = () => {
    if (isDirty) {
      setApproveConfirmOpen(true);
    } else {
      reviewMutation.mutate({ action: 'approve' });
    }
  };

  const handleReject = () => {
    setRejectConfirmOpen(true);
  };

  if (!suggestion) return null;

  const triggerLabel =
    translateSuggestionModal(`admin:suggestions.triggers.${suggestion.triggerType}`) ??
    suggestion.triggerType;
  const channelLabel =
    translateSuggestionModal(`admin:suggestions.channels.${suggestion.channel}`) ??
    suggestion.channel;
  const segmentColor = suggestion.clientSegment
    ? (SEGMENT_COLORS[suggestion.clientSegment] ?? colors.textMuted)
    : colors.textMuted;

  return (
    <>
      <AppModal
        open={open}
        onClose={onClose}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing[2], flexWrap: 'wrap' }}>
            <Send size={18} color={colors.accent} />
            <span>{translateSuggestionModal('admin:suggestionModal.reviewSuggestedMessage')}</span>
          </Box>
        }
        size="lg"
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: spacing[3],
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <AppButton variant="ghost" onClick={handleReject} disabled={reviewMutation.isPending}>
              {t('admin:suggestions.reject')}
            </AppButton>
            <AppButton variant="primary" onClick={handleApprove} loading={reviewMutation.isPending}>
              {translateSuggestionModal('admin:suggestionModal.approveAndSend')}
            </AppButton>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
          {/* ── Client strip ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              padding: spacing[3],
              backgroundColor: colors.bg,
              borderRadius: radius.md,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: fonts.body,
                  fontWeight: fontWeights.semibold,
                  fontSize: '15px',
                  color: colors.textPrimary,
                }}
              >
                {suggestion.clientName}
              </Typography>
            </Box>

            {/* Segment badge */}
            {suggestion.clientSegment && (
              <Chip
                label={translateSuggestionModal(
                  `admin:clientDetail.segments.${suggestion.clientSegment}`
                )}
                size="small"
                sx={{
                  backgroundColor: segmentColor + '22',
                  color: segmentColor,
                  fontWeight: fontWeights.bold,
                  fontSize: '10px',
                  letterSpacing: '0.04em',
                  height: 20,
                }}
              />
            )}

            {/* Trigger reason */}
            <Chip
              label={triggerLabel}
              size="small"
              sx={{
                backgroundColor: colors.accentLighter ?? colors.bg,
                color: colors.accent,
                fontWeight: fontWeights.semibold,
                fontSize: '11px',
                height: 22,
              }}
            />

            {/* Channel */}
            <Chip
              label={channelLabel}
              size="small"
              variant="outlined"
              sx={{
                borderColor: colors.border,
                color: colors.textSecondary,
                fontSize: '11px',
                height: 22,
              }}
            />
          </Box>

          {/* ── Email subject (if applicable) ── */}
          {(suggestion.channel === 'email' || suggestion.channel === 'both') &&
            suggestion.subject && (
              <Box>
                <Typography
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '12px',
                    fontWeight: fontWeights.semibold,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: spacing[1],
                  }}
                >
                  {translateSuggestionModal('admin:suggestionModal.subject')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: fonts.body,
                    fontSize: '14px',
                    color: colors.textPrimary,
                    padding: `${spacing[2]} ${spacing[3]}`,
                    backgroundColor: colors.bg,
                    borderRadius: radius.md,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {suggestion.subject}
                </Typography>
              </Box>
            )}

          {/* ── Editable body ── */}
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontSize: '12px',
                fontWeight: fontWeights.semibold,
                color: colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: spacing[1],
              }}
            >
              {translateSuggestionModal('admin:suggestionModal.message')}
              {isDirty ? ` ${translateSuggestionModal('admin:suggestionModal.editedSuffix')}` : ''}
            </Typography>
            <TextField
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={reviewMutation.isPending}
              inputProps={{ maxLength: 2000 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: fonts.body,
                  fontSize: '14px',
                  backgroundColor: colors.surface,
                  borderRadius: radius.md,
                  '& fieldset': { borderColor: isDirty ? colors.accent : colors.border },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
              }}
            />
            <Typography
              sx={{
                fontFamily: fonts.body,
                fontSize: '11px',
                color: colors.textMuted,
                textAlign: 'right',
                marginTop: '4px',
              }}
            >
              {body.length} / 2000
            </Typography>
          </Box>
        </Box>
      </AppModal>

      {/* Reject confirmation */}
      <AppConfirmModal
        open={rejectConfirmOpen}
        onClose={() => setRejectConfirmOpen(false)}
        onConfirm={() => {
          setRejectConfirmOpen(false);
          reviewMutation.mutate({ action: 'reject' });
        }}
        title={translateSuggestionModal('admin:suggestionModal.confirmRejectTitle')}
        message={translateSuggestionModal('admin:suggestionModal.confirmRejectMessage')}
        confirmLabel={t('admin:suggestions.reject')}
        variant="danger"
        loading={reviewMutation.isPending}
      />

      {/* Approve with edited body confirmation */}
      <AppConfirmModal
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        onConfirm={() => {
          setApproveConfirmOpen(false);
          reviewMutation.mutate({ action: 'approve', editedBody: body });
        }}
        title={translateSuggestionModal('admin:suggestionModal.confirmApproveTitle')}
        message={translateSuggestionModal('admin:suggestionModal.confirmApproveMessage')}
        confirmLabel={translateSuggestionModal('admin:suggestionModal.confirmApproveLabel')}
        loading={reviewMutation.isPending}
      />
    </>
  );
}
